// src/settings/ProfileSettings.js
// The user's own settings — every role gets this page. For now: who you are,
// and your companion (the animated buddy on empty-state pages).
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Check } from 'lucide-react';
import { API_BASE } from '../apiBase';
import Companion, { COMPANIONS } from '../lib/Companion';

function ProfileSettings() {
  const [me, setMe] = useState(null);
  const [companion, setCompanion] = useState('knight');
  const [saved, setSaved] = useState(false);

  // Email signature — appended to every Outlook draft the app creates
  // (invoices, quotes, proposals, receipts). Superadmin-saved, portal-wide.
  const [sig, setSig] = useState('');
  const [sigSaved, setSigSaved] = useState(false);
  const [sigBusy, setSigBusy] = useState(false);
  const isSuperAdmin = localStorage.getItem('role') === 'superadmin';
  const saveSig = async () => {
    if (sigBusy) return;
    setSigBusy(true);
    try {
      await axios.put(`${API_BASE}/portal-settings`, { key: 'emailSignature', value: sig },
        { headers: { Authorization: localStorage.getItem('token') } });
      setSigSaved(true);
      setTimeout(() => setSigSaved(false), 2000);
    } catch (e) { alert(e.response?.data?.error || 'Could not save the signature.'); }
    finally { setSigBusy(false); }
  };

  useEffect(() => {
    axios.get(`${API_BASE}/me`, { headers: { Authorization: localStorage.getItem('token') } })
      .then(r => { setMe(r.data); setCompanion(r.data?.companion || 'knight'); })
      .catch(() => {});
    axios.get(`${API_BASE}/portal-settings`, { headers: { Authorization: localStorage.getItem('token') } })
      .then(r => setSig(String(r.data?.emailSignature || '')))
      .catch(() => {});
  }, []);

  const pick = async (key) => {
    setCompanion(key);
    setSaved(false);
    try {
      await axios.put(`${API_BASE}/me/companion`, { companion: key },
        { headers: { Authorization: localStorage.getItem('token') } });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (_) { /* best effort */ }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-zinc-100 p-8 max-md:p-3 space-y-6">
      <div>
        <h1 className="text-3xl max-md:text-xl font-bold tracking-tight">Profile</h1>
        <p className="text-sm text-zinc-500 mt-1">Your account and personal preferences.</p>
      </div>

      {/* Who you are */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800/50 shadow-2xl p-6 max-md:p-4 max-w-2xl">
        {me ? (
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-indigo-900/60 flex items-center justify-center text-indigo-200 font-bold">
              {(me.name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-lg font-bold text-white truncate">{me.name}</p>
              <p className="text-sm text-zinc-500 break-all">{me.email}</p>
              <p className="text-[11px] text-zinc-600 uppercase font-bold mt-0.5">{me.role}</p>
            </div>
          </div>
        ) : (
          <p className="text-zinc-600 text-sm">Loading…</p>
        )}
        <p className="text-[11px] text-zinc-600 mt-4">Need your name, email, or password changed? Ask an admin — they manage accounts under Settings › Users.</p>
      </div>

      {/* Email signature — baked into app-created Outlook drafts */}
      {isSuperAdmin && (
        <div className="bg-gray-900 rounded-2xl border border-gray-800/50 shadow-2xl p-6 max-md:p-4 max-w-2xl">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-bold">Email Signature</h2>
            {sigSaved && <span className="text-xs font-semibold text-emerald-400 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Saved</span>}
          </div>
          <p className="text-xs text-zinc-500 mb-3">Appended to every Outlook draft the app creates — invoices, quotes, proposals, receipts. Outlook can't add your signature to drafts made through the API, so paste it here once.</p>
          <textarea
            rows={6}
            value={sig}
            onChange={(e) => setSig(e.target.value)}
            placeholder={'Bryant\nStalwart Electric, Inc.\nCSLB #1109509 · C-10 Electrical\n916-860-4198 · bryant@stalwart-electric.com\nstalwart-electric.com'}
            className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-indigo-600/60 resize-y font-mono"
          />
          <button onClick={saveSig} disabled={sigBusy}
            className="mt-3 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 rounded-xl text-sm font-semibold text-white">
            {sigBusy ? 'Saving…' : 'Save Signature'}
          </button>
        </div>
      )}

      {/* Companion picker */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800/50 shadow-2xl p-6 max-md:p-4 max-w-2xl">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-bold">Your Companion</h2>
          {saved && <span className="text-xs font-semibold text-emerald-400 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Saved</span>}
        </div>
        <p className="text-xs text-zinc-500 mb-5">Your little buddy keeps you company when a page has nothing to show yet.</p>
        <div className="grid grid-cols-3 max-md:grid-cols-1 gap-3">
          {Object.entries(COMPANIONS).map(([key, c]) => {
            const on = companion === key;
            return (
              <button
                key={key}
                onClick={() => pick(key)}
                className={`rounded-2xl border p-4 flex flex-col items-center gap-1 transition-all ${
                  on ? 'border-indigo-500/70 bg-indigo-600/10 shadow-lg shadow-indigo-900/20' : 'border-gray-800/70 bg-gray-950/40 hover:border-gray-600'
                }`}
              >
                <Companion kind={key} size={110} />
                <p className={`text-sm font-bold ${on ? 'text-indigo-300' : 'text-zinc-200'}`}>{c.label}</p>
                <p className="text-[11px] text-zinc-500">{c.sub}</p>
                {on && <span className="mt-1 text-[10px] font-bold uppercase text-indigo-400">Selected</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ProfileSettings;
